// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/planning/USER_MANAGEMENT_UI_PLAN_COMPLETE.md
/**
 * User Statistics Component
 * 
 * Displays key metrics and statistics about users:
 * - Total users
 * - Active/Inactive users
 * - Users by role
 * - Recent activity
 */

import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

const UserStatistics = ({ users }) => {
  // Calculate statistics
  const totalUsers = users.length;
  const activeUsers = users.filter(user => user.is_active).length;
  const inactiveUsers = totalUsers - activeUsers;
  
  // Count users by role
  const roleCounts = users.reduce((acc, user) => {
    const role = user.role || 'Unknown';
    acc[role] = (acc[role] || 0) + 1;
    return acc;
  }, {});

  // Calculate percentages
  const activePercentage = totalUsers > 0 ? (activeUsers / totalUsers) * 100 : 0;
  const inactivePercentage = totalUsers > 0 ? (inactiveUsers / totalUsers) * 100 : 0;

  const stats = [
    {
      title: 'Total Users',
      value: totalUsers,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: '#13213c',
      subtitle: 'All registered users'
    },
    {
      title: 'Active Users',
      value: activeUsers,
      icon: <PersonAddIcon sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
      subtitle: `${activePercentage.toFixed(1)}% of total`,
      progress: activePercentage
    },
    {
      title: 'Inactive Users',
      value: inactiveUsers,
      icon: <WarningIcon sx={{ fontSize: 40 }} />,
      color: '#d32f2f',
      subtitle: `${inactivePercentage.toFixed(1)}% of total`,
      progress: inactivePercentage
    },
    {
      title: 'User Roles',
      value: Object.keys(roleCounts).length,
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      color: '#1976d2',
      subtitle: 'Different role types'
    }
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {stats.map((stat, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card 
            sx={{ 
              height: '100%',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box 
                  sx={{ 
                    p: 1.5,
                    borderRadius: '12px',
                    bgcolor: `${stat.color}15`,
                    color: stat.color,
                    mr: 2
                  }}
                >
                  {stat.icon}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="h4" 
                    component="div"
                    sx={{ 
                      fontWeight: 700,
                      color: stat.color,
                      lineHeight: 1
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {stat.title}
                  </Typography>
                </Box>
              </Box>
              
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {stat.subtitle}
              </Typography>
              
              {stat.progress !== undefined && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={stat.progress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: `${stat.color}20`,
                      '& .MuiLinearProgress-bar': {
                        bgcolor: stat.color,
                        borderRadius: 3
                      }
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default UserStatistics;
